import { useCallback } from "react";
import { exportSVGtoPNG } from "../../../utils/utility";
import { generateCSV, downloadXLS } from "../utils";

/**
 * Hook that provides chart export action handlers.
 * @param {React.RefObject} containerRef - Ref to the chart container for fullscreen
 * @param {React.RefObject} svgRef - Ref to the SVG element for PNG export
 * @param {object} allSeriesData - All series data keyed by SERIES_CONFIG.key
 * @param {Function} closeMenu - Callback to close the context menu after action
 * @returns {{ handleFullscreen, handleDownloadCSV, handleDownloadXLS, handleDownloadPNG }}
 */
export default function useChartExport(containerRef, svgRef, allSeriesData, closeMenu, siteTimezone) {
  const handleFullscreen = useCallback(() => {
    const el = containerRef.current?.parentElement?.parentElement;
    if (el) {
      if (document.fullscreenElement) {
        document.exitFullscreen();
      } else {
        el.requestFullscreen();
      }
    }
    closeMenu();
  }, [containerRef, closeMenu]);

  const handleDownloadCSV = useCallback(() => {
    const csv = generateCSV(allSeriesData, siteTimezone);
    const blob = new Blob([csv], { type: "text/csv" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = "chart.csv";
    a.click();
    URL.revokeObjectURL(a.href);
    closeMenu();
  }, [allSeriesData, closeMenu, siteTimezone]);

  const handleDownloadXLS = useCallback(() => {
    downloadXLS(allSeriesData, siteTimezone);
    closeMenu();
  }, [allSeriesData, closeMenu, siteTimezone]);

  const handleDownloadPNG = useCallback(() => {
    if (svgRef.current) {
      exportSVGtoPNG(svgRef.current, "chart.png");
    }
    closeMenu();
  }, [svgRef, closeMenu]);

  return { handleFullscreen, handleDownloadCSV, handleDownloadXLS, handleDownloadPNG };
}
